/**
 * IFRAP-AI.fr - Full-screen TV Counters Page
 * Standalone page for TV/screen display of real-time counters
 * Can be accessed directly at /compteurs
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { X, TrendingUp, TrendingDown, Monitor, Share2, Code, Check, Copy, ArrowLeft, Home, AlertTriangle, Clock } from 'lucide-react';

const LIVE_COUNTERS_CONFIG = {
  depense_publique: {
    label: 'Dépenses publiques',
    annualAmount: 1_740_000_000_000,
    annualAmountN1: 1_667_000_000_000,
    unit: '\u20ac',
    descriptions: {
      realtime: 'dépensés depuis minuit',
      day: 'dépensés par jour',
      '30days': 'dépensés sur 30 jours',
      year: 'dépensés par an',
    },
    color: '#4F8EF7',
    dataDate: '2025-12-31',
    dataPeriod: 'Année 2025',
    source: 'INSEE - Comptes nationaux',
  },
  dette_seconde: {
    label: 'Endettement net',
    annualAmount: 154_000_000_000,
    annualAmountN1: 150_000_000_000,
    unit: '\u20ac',
    descriptions: {
      realtime: 'de dette nouvelle depuis minuit',
      day: 'de dette nouvelle par jour',
      '30days': 'de dette nouvelle sur 30 jours',
      year: 'de dette nouvelle par an',
    },
    color: '#E1000F',
    dataDate: '2025-12-31',
    dataPeriod: 'Année 2025',
    source: 'INSEE - Dette Maastricht',
  },
  deficit_commercial: {
    label: 'Déficit commercial',
    annualAmount: 69_200_000_000,  // MISE À JOUR : 69,2 Mds€ (Douanes 2025)
    annualAmountN1: 79_200_000_000, // 2024
    unit: '\u20ac',
    descriptions: {
      realtime: "d'importations nettes depuis minuit",
      day: "d'importations nettes par jour",
      '30days': "d'importations nettes sur 30 jours",
      year: "d'importations nettes par an",
    },
    color: '#F59E0B',
    dataDate: '2025-12-31',
    dataPeriod: 'Année 2025',
    source: 'Douanes françaises',
  },
  eloignements: {
    label: 'Éloignements (OQTF)',
    annualAmount: 32_379,  // 2025 : 32 379 éloignements exécutés (+16,5%)
    annualAmountN1: 27_807, // 2024
    unit: '',
    descriptions: {
      realtime: "personnes éloignées depuis le 1er janvier",
      day: "éloignements par jour en moyenne",
      '30days': "éloignements sur 30 jours",
      year: "éloignements par an",
    },
    color: '#8B5CF6',
    dataDate: '2025-12-31',
    dataPeriod: 'Année 2025',
    source: 'Ministère de l\'Intérieur',
    isCount: true,
  },
};

// Get data periods for display
const getDataPeriods = () => {
  const periods = [];
  Object.values(LIVE_COUNTERS_CONFIG).forEach(config => {
    if (config.dataPeriod && !periods.includes(config.dataPeriod)) {
      periods.push(config.dataPeriod);
    }
  });
  return periods;
};

// Data Period Info Component (shows data sources period, not "outdated" warning)
const DataAgeAlert = () => {
  const periods = getDataPeriods();
  
  if (periods.length === 0) return null;
  
  // All same period
  const uniquePeriod = periods.length === 1 ? periods[0] : null;
  
  return (
    <div 
      className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-medium bg-blue-500/20 border border-blue-500/30 text-blue-300"
      data-testid="data-age-alert"
    >
      <Clock className="w-4 h-4 flex-shrink-0" />
      {uniquePeriod ? (
        <span>Données <strong>{uniquePeriod}</strong></span>
      ) : (
        <span>Données <strong>{periods.join(' / ')}</strong></span>
      )}
    </div>
  );
};

const TIME_PERIODS = [
  { key: 'realtime', label: 'Temps réel' },
  { key: 'day', label: "Aujourd'hui" },
  { key: '30days', label: '30 jours' },
  { key: 'year', label: 'Année' },
];

const formatNumberTV = (num) => {
  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(6)} milliards`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(4)} millions`;
  return `${Math.floor(num).toLocaleString('fr-FR')}`;
};

const formatPerSecond = (annual, isCount = false) => {
  if (isCount) {
    // For counts, show per day instead of per second
    const perDay = annual / 365;
    return `${perDay.toFixed(1)}/jour`;
  }
  const perSec = annual / (365 * 24 * 3600);
  return `${Math.round(perSec).toLocaleString('fr-FR')} \u20ac/sec`;
};

const formatCompact = (num) => {
  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(1)} milliards`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)} millions`;
  return `${Math.floor(num).toLocaleString('fr-FR')}`;
};

const TVCounter = ({ config, period, onClick, isSelected }) => {
  const [amount, setAmount] = useState(0);
  const ratePerMs = config.annualAmount / (365 * 24 * 3600 * 1000);
  
  // Get description based on period
  const description = config.descriptions ? config.descriptions[period] : config.description;

  useEffect(() => {
    if (period !== 'realtime') {
      const multipliers = {
        day: 24 * 3600 * 1000,
        '30days': 30 * 24 * 3600 * 1000,
        year: 365 * 24 * 3600 * 1000,
      };
      setAmount((multipliers[period] || 0) * ratePerMs);
      return;
    }

    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    const startMs = midnight.getTime();

    const tick = () => setAmount((Date.now() - startMs) * ratePerMs);
    tick();
    const interval = setInterval(tick, 50);
    return () => clearInterval(interval);
  }, [ratePerMs, period]);

  const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
  const ytd = (config.annualAmount / 365) * dayOfYear;
  const ytdN1 = (config.annualAmountN1 / 365) * dayOfYear;
  const pct = ytdN1 ? ((ytd - ytdN1) / ytdN1 * 100) : 0;

  return (
    <div
      className={`flex flex-col items-center justify-center cursor-pointer rounded-2xl p-4 sm:p-6 md:p-8 transition-all ${
        isSelected ? 'bg-white/10 ring-1 ring-white/20' : 'hover:bg-white/5'
      }`}
      onClick={onClick}
      data-testid={`tv-standalone-counter-${config.label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <span className="text-white/50 text-xs sm:text-sm md:text-base uppercase tracking-[0.15em] sm:tracking-[0.2em] mb-2 sm:mb-3 font-medium text-center">
        {config.label}
      </span>
      
      {/* Number + Unit together */}
      <div className="text-center">
        <span
          className="font-black tabular-nums block"
          style={{ fontSize: 'clamp(2rem, 8vw, 5rem)', color: config.color, lineHeight: 1.1 }}
        >
          {formatNumberTV(amount)}
        </span>
        <span className="text-white/50 text-lg sm:text-xl md:text-2xl font-medium">{config.unit}</span>
      </div>
      
      <p className="text-white/40 text-xs sm:text-sm mt-2 text-center">{description}</p>
      
      <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-white/30 text-[10px] sm:text-xs mt-2 sm:mt-3">
        <span className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-red-500 animate-pulse" />
          {formatPerSecond(config.annualAmount, config.isCount)}
        </span>
        <span className="hidden sm:inline">|</span>
        <span className={`flex items-center gap-1 font-semibold ${pct > 0 ? 'text-red-400/60' : 'text-green-400/60'}`}>
          {pct > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {pct > 0 ? '+' : ''}{pct.toFixed(1)}% vs N-1
        </span>
      </div>

      {/* Detail on selection */}
      {isSelected && (
        <div className="mt-4 sm:mt-6 bg-white/5 backdrop-blur-sm rounded-lg p-3 sm:p-4 border border-white/10 w-full max-w-sm">
          <DetailTable config={config} />
        </div>
      )}
    </div>
  );
};

const DetailTable = ({ config }) => {
  const now = new Date();
  const ratePerDay = config.annualAmount / 365;
  const ratePerDayN1 = config.annualAmountN1 / 365;
  const dayOfYear = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / 86400000);
  const unit = config.isCount ? '' : '\u20ac';

  const rows = [
    { label: "Aujourd'hui", v: ratePerDay, vn1: ratePerDayN1 },
    { label: 'Mois', v: ratePerDay * now.getDate(), vn1: ratePerDayN1 * now.getDate() },
    { label: 'Depuis janv.', v: ratePerDay * dayOfYear, vn1: ratePerDayN1 * dayOfYear },
    { label: 'Annuel', v: config.annualAmount, vn1: config.annualAmountN1 },
  ];

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-white/40">
          <th className="text-left pb-1">Période</th>
          <th className="text-right pb-1">2025</th>
          <th className="text-right pb-1">N-1</th>
          <th className="text-right pb-1">Écart</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => {
          const d = r.v - r.vn1;
          const p = r.vn1 ? (d / r.vn1 * 100) : 0;
          return (
            <tr key={r.label} className="border-t border-white/5">
              <td className="py-1 text-white/60">{r.label}</td>
              <td className="py-1 text-right text-white font-mono">{formatCompact(r.v)}{unit}</td>
              <td className="py-1 text-right text-white/40 font-mono">{formatCompact(r.vn1)}{unit}</td>
              <td className={`py-1 text-right font-semibold ${p > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {p > 0 ? '+' : ''}{p.toFixed(1)}%
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const TVCountersPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState(searchParams.get('period') || 'realtime');
  const [selected, setSelected] = useState(null);
  const [clock, setClock] = useState(new Date());
  const [slideshowActive, setSlideshowActive] = useState(false);
  const [slideshowIndex, setSlideshowIndex] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(null);
  const counterKeys = Object.keys(LIVE_COUNTERS_CONFIG);

  // Update URL when period changes
  useEffect(() => {
    setSearchParams({ period });
  }, [period, setSearchParams]);

  // Get current page URL with period
  const pageUrl = typeof window !== 'undefined' 
    ? `${window.location.origin}/compteurs?period=${period}` 
    : `https://www.ifrapai.org/compteurs?period=${period}`;
  const embedCode = `<iframe src="${pageUrl.replace('?period=', '?embed=true&period=')}" width="100%" height="500" frameborder="0" style="border-radius: 12px; overflow: hidden;"></iframe>`;

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  useEffect(() => {
    const interval = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Slideshow auto-cycle
  useEffect(() => {
    if (!slideshowActive) return;
    const interval = setInterval(() => {
      setSlideshowIndex(prev => (prev + 1) % counterKeys.length);
    }, 8000); // 8 seconds per counter
    return () => clearInterval(interval);
  }, [slideshowActive, counterKeys.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#08082a] via-[#0a0a35] to-[#0f0f40] flex flex-col" data-testid="tv-counters-page">
      {/* Header - Mobile Optimized */}
      <div className="px-4 md:px-8 py-4 md:py-6 border-b border-white/5">
        {/* Top row: Back, Logo, Share */}
        <div className="flex items-center justify-between mb-4 md:mb-0">
          {/* Back button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 text-white/60 hover:text-white hover:bg-white/10 border border-white/10 transition-all"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium hidden sm:inline">Retour</span>
          </button>

          {/* Logo - Compact on mobile */}
          <div className="flex items-center gap-2 md:gap-4">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-lg md:rounded-xl flex items-center justify-center shadow-lg shadow-white/10">
              <span className="text-[#000091] font-black text-base md:text-lg tracking-tight">IF</span>
            </div>
            <div>
              <span className="text-white font-bold text-lg md:text-xl tracking-tight">IFRAP-AI</span>
              <span className="text-white/30 text-[10px] md:text-xs block">Compteurs temps réel</span>
            </div>
          </div>

          {/* Share button */}
          <button
            onClick={() => setShowShareModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 text-white/60 hover:text-white hover:bg-white/10 border border-white/10 transition-all"
            data-testid="share-btn"
          >
            <Share2 className="w-4 h-4" />
            <span className="text-sm font-medium hidden sm:inline">Partager</span>
          </button>
        </div>

        {/* Data Age Alert */}
        <div className="flex justify-center my-3 md:my-4">
          <DataAgeAlert />
        </div>

        {/* Bottom row: Period selector + Clock (Desktop: inline, Mobile: stacked) */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0 md:mt-4">
          {/* Period toggle - Scrollable on mobile */}
          <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 -mx-1 px-1">
            {TIME_PERIODS.map((tp) => (
              <button
                key={tp.key}
                onClick={() => setPeriod(tp.key)}
                className={`px-4 py-2 text-sm rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${
                  period === tp.key
                    ? 'bg-[#E1000F] text-white font-bold shadow-lg shadow-red-500/20'
                    : 'bg-white/5 text-white/50 hover:text-white border border-white/10'
                }`}
                data-testid={`tv-page-period-${tp.key}`}
              >
                {tp.label}
              </button>
            ))}
            
            {/* Slideshow toggle - in period row on mobile */}
            <button
              onClick={() => { setSlideshowActive(!slideshowActive); setSlideshowIndex(0); }}
              className={`px-4 py-2 text-sm rounded-lg transition-all whitespace-nowrap flex-shrink-0 flex items-center gap-1.5 ${
                slideshowActive
                  ? 'bg-purple-600 text-white font-bold shadow-lg shadow-purple-500/20'
                  : 'bg-white/5 text-white/50 hover:text-white border border-white/10'
              }`}
              data-testid="slideshow-toggle"
            >
              <Monitor className="w-4 h-4" />
              <span className="hidden sm:inline">{slideshowActive ? 'Stop' : 'Diaporama'}</span>
            </button>
          </div>

          {/* Clock - Hidden on mobile, visible on tablet+ */}
          <div className="hidden md:block text-right">
            <div className="text-white/80 font-mono text-xl tabular-nums">
              {clock.toLocaleTimeString('fr-FR')}
            </div>
            <div className="text-white/30 text-xs">
              {clock.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          </div>
        </div>
      </div>

      {/* Counters - Normal or Slideshow mode */}
      <div className="flex-1 flex items-center justify-center px-8">
        {slideshowActive ? (
          /* SLIDESHOW MODE - One counter at a time */
          <div className="w-full max-w-3xl">
            {counterKeys.map((key, idx) => (
              <div
                key={key}
                className={`transition-all duration-1000 ${
                  idx === slideshowIndex
                    ? 'opacity-100 scale-100'
                    : 'opacity-0 scale-95 absolute pointer-events-none'
                }`}
              >
                {idx === slideshowIndex && (
                  <div className="flex flex-col items-center justify-center">
                    <TVCounter
                      config={LIVE_COUNTERS_CONFIG[key]}
                      period={period}
                      onClick={() => {}}
                      isSelected={true}
                    />
                    {/* Dots indicator */}
                    <div className="flex items-center gap-3 mt-8">
                      {counterKeys.map((_, dotIdx) => (
                        <button
                          key={dotIdx}
                          onClick={() => setSlideshowIndex(dotIdx)}
                          className={`w-3 h-3 rounded-full transition-all ${
                            dotIdx === slideshowIndex
                              ? 'bg-[#E1000F] scale-125'
                              : 'bg-white/20 hover:bg-white/40'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          /* NORMAL MODE - Stack on mobile, side by side on desktop */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 lg:gap-8 max-w-7xl w-full px-4 md:px-8">
            {counterKeys.map((key) => (
              <TVCounter
                key={key}
                config={LIVE_COUNTERS_CONFIG[key]}
                period={period}
                onClick={() => setSelected(selected === key ? null : key)}
                isSelected={selected === key}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 md:px-8 py-3 md:py-4 border-t border-white/5">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-white/20 text-[10px] sm:text-xs text-center sm:text-left">
            Calcul temps réel (Eurostat, INSEE, Douanes)
          </span>
        </div>
        <a
          href="/"
          className="text-white/30 text-[10px] sm:text-xs hover:text-white/60 transition-colors"
        >
          Retour au tableau de bord
        </a>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setShowShareModal(false)}
        >
          <div 
            className="bg-gradient-to-br from-[#1a1a3a] to-[#0f0f2a] border border-white/10 rounded-2xl p-8 max-w-xl w-full mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Share2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Partager les compteurs</h3>
                  <p className="text-white/40 text-sm">Diffusez les chiffres en temps réel</p>
                </div>
              </div>
              <button 
                onClick={() => setShowShareModal(false)}
                className="text-white/40 hover:text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Direct link */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="text-white font-medium text-sm flex items-center gap-2">
                  🔗 Lien direct
                </label>
                <span className="text-white/30 text-xs">Partagez cette URL</span>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={pageUrl}
                  className="flex-1 px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm font-mono focus:outline-none focus:border-blue-500"
                />
                <button
                  onClick={() => copyToClipboard(pageUrl, 'link')}
                  className={`px-5 py-3 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 ${
                    copied === 'link'
                      ? 'bg-green-500 text-white'
                      : 'bg-blue-600 text-white hover:bg-blue-500'
                  }`}
                >
                  {copied === 'link' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied === 'link' ? 'Copié !' : 'Copier'}
                </button>
              </div>
            </div>

            {/* Embed code */}
            <div className="mb-6 p-4 bg-black/30 rounded-xl border border-white/5">
              <div className="flex items-center justify-between mb-3">
                <label className="text-white font-medium text-sm flex items-center gap-2">
                  <Code className="w-4 h-4 text-purple-400" />
                  Intégrer sur votre site
                </label>
                <button
                  onClick={() => copyToClipboard(embedCode, 'embed')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                    copied === 'embed'
                      ? 'bg-green-500 text-white'
                      : 'bg-purple-600 text-white hover:bg-purple-500'
                  }`}
                >
                  {copied === 'embed' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied === 'embed' ? 'Copié !' : 'Copier le code'}
                </button>
              </div>
              <textarea
                readOnly
                value={embedCode}
                rows={3}
                className="w-full px-3 py-2 bg-black/50 border border-white/10 rounded-lg text-white/70 text-xs font-mono resize-none focus:outline-none"
              />
              <p className="text-white/40 text-xs mt-2">
                Collez ce code HTML dans votre article ou site web pour afficher les compteurs IFRAP en direct.
              </p>
            </div>

            {/* Social share buttons */}
            <div>
              <label className="block text-white font-medium text-sm mb-3">Partager sur les réseaux</label>
              <div className="grid grid-cols-3 gap-3">
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent('📊 Les compteurs en temps réel des finances publiques françaises par @FondationIFRAP\n\n💶 Dépenses, dette, déficit commercial... suivez les chiffres seconde par seconde !')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-3.5 bg-black/40 hover:bg-[#1DA1F2]/20 border border-white/10 hover:border-[#1DA1F2]/50 text-white rounded-xl text-sm font-medium text-center transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  Twitter / X
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-3.5 bg-black/40 hover:bg-[#0A66C2]/20 border border-white/10 hover:border-[#0A66C2]/50 text-white rounded-xl text-sm font-medium text-center transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  LinkedIn
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-3.5 bg-black/40 hover:bg-[#1877F2]/20 border border-white/10 hover:border-[#1877F2]/50 text-white rounded-xl text-sm font-medium text-center transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  Facebook
                </a>
              </div>
            </div>

            {/* Footer tip */}
            <div className="mt-6 pt-4 border-t border-white/10 text-center">
              <p className="text-white/30 text-xs">
                💡 Astuce : Le lien conserve la période sélectionnée (temps réel, 30 jours, etc.)
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TVCountersPage;
